import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailApi, EmailAccount, AccountCreatePayload } from '../../services/emailApi';
import './EmailSettings.css';

const EmailSettings: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [editing, setEditing] = useState<EmailAccount | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const list = await emailApi.listAccounts();
      setAccounts(list);
      setLoading(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || String(err));
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить аккаунт? Письма из локальной БД будут удалены вместе с аккаунтом.')) return;
    await emailApi.deleteAccount(id);
    refresh();
  };

  const handleTest = async (id: number) => {
    try {
      const result = await emailApi.testAccount(id);
      const msg = `IMAP: ${result.imap.message}\nSMTP: ${result.smtp.message}`;
      if (result.ok) alert('✅ Подключение работает:\n\n' + msg);
      else alert('❌ Ошибка подключения:\n\n' + msg);
      refresh();
    } catch (err: any) {
      alert('❌ Test failed: ' + (err?.response?.data?.detail || String(err)));
    }
  };

  if (loading) return <div style={{ padding: 40 }}>Загрузка...</div>;

  return (
    <div className="email-settings">
      <div className="email-settings-header">
        <button className="back-btn" onClick={() => navigate('/mail')}>
          <i className="bi bi-arrow-left"></i> К почте
        </button>
        <h1>
          <i className="bi bi-envelope-fill"></i> Настройка почтовых аккаунтов
        </h1>
        <button className="btn-primary" onClick={() => setEditing('new')}>
          <i className="bi bi-plus-lg"></i> Добавить аккаунт
        </button>
      </div>

      {error && <div className="email-settings-error">⚠️ {error}</div>}

      {accounts.length === 0 ? (
        <div className="email-settings-empty">
          <p>Аккаунтов пока нет. Добавьте первый — IMAP/SMTP сервера определятся автоматически.</p>
        </div>
      ) : (
        <table className="email-settings-table">
          <thead>
            <tr>
              <th>Email / Label</th>
              <th>IMAP</th>
              <th>SMTP</th>
              <th>Активен</th>
              <th>Sync</th>
              <th>Статус</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id}>
                <td data-label="Аккаунт">
                  <div className="email-label">{a.label || a.email}</div>
                  <div className="email-sub">{a.email}</div>
                  {a.is_default && <span className="badge">по умолчанию</span>}
                </td>
                <td data-label="IMAP">
                  {a.imap_host}:{a.imap_port}
                  {a.imap_ssl ? ' SSL' : ''}
                </td>
                <td data-label="SMTP">
                  {a.smtp_host}:{a.smtp_port}
                  {a.smtp_ssl ? ' SSL' : ''}
                  {a.smtp_starttls ? ' STARTTLS' : ''}
                </td>
                <td data-label="Активен">{a.is_active ? '✅' : '⏸'}</td>
                <td data-label="Sync">{a.last_sync_at ? new Date(a.last_sync_at).toLocaleString('ru-RU') : '—'}</td>
                <td data-label="Статус">
                  {a.last_sync_status === 'ok' && (
                    <span className="status-dot status-ok" title="Подключение работает">●</span>
                  )}
                  {a.last_sync_status === 'error' && (
                    <span className="status-dot status-error" title={a.last_sync_error || ''}>●</span>
                  )}
                  {!a.last_sync_status && (
                    <span className="status-dot status-unknown" title="Не проверялся">●</span>
                  )}
                </td>
                <td className="actions-cell">
                  <button className="icon-btn" onClick={() => handleTest(a.id)} title="Проверить">
                    <i className="bi bi-arrow-repeat"></i>
                  </button>
                  <button className="icon-btn" onClick={() => setEditing(a)} title="Редактировать">
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button className="icon-btn danger" onClick={() => handleDelete(a.id)} title="Удалить">
                    <i className="bi bi-trash3"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <AccountForm
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

// ── Form ─────────────────────────────────────────────────────────

interface FormProps {
  account: EmailAccount | null;
  onClose: () => void;
  onSaved: () => void;
}

const AccountForm: React.FC<FormProps> = ({ account, onClose, onSaved }) => {
  const isNew = !account;
  const [email, setEmail] = useState(account?.email || '');
  const [password, setPassword] = useState('');
  const [label, setLabel] = useState(account?.label || '');
  const [displayName, setDisplayName] = useState(account?.display_name || '');
  const [signatureText, setSignatureText] = useState(account?.signature_text || '');
  const [signatureHtml, setSignatureHtml] = useState(account?.signature_html || '');
  const [imapHost, setImapHost] = useState(account?.imap_host || '');
  const [imapPort, setImapPort] = useState(account?.imap_port ?? 993);
  const [imapSsl, setImapSsl] = useState(account?.imap_ssl ?? true);
  const [smtpHost, setSmtpHost] = useState(account?.smtp_host || '');
  const [smtpPort, setSmtpPort] = useState(account?.smtp_port ?? 465);
  const [smtpSsl, setSmtpSsl] = useState(account?.smtp_ssl ?? true);
  const [smtpStarttls, setSmtpStarttls] = useState(account?.smtp_starttls ?? false);
  const [isDefault, setIsDefault] = useState(account?.is_default ?? false);
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAutodetect = async () => {
    if (!email.includes('@')) {
      setError('Сначала введите email');
      return;
    }
    try {
      const preset = await emailApi.autodetect(email);
      setImapHost(preset.imap_host);
      setImapPort(preset.imap_port);
      setImapSsl(preset.imap_ssl);
      setSmtpHost(preset.smtp_host);
      setSmtpPort(preset.smtp_port);
      setSmtpSsl(preset.smtp_ssl);
      setSmtpStarttls(preset.smtp_starttls);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Провайдер не распознан — заполните вручную');
      setAdvanced(true);
    }
  };

  const handleSave = async () => {
    if (!email.trim()) {
      setError('Email обязателен');
      return;
    }
    if (isNew && !password.trim()) {
      setError('Пароль обязателен для нового аккаунта');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: AccountCreatePayload = {
        email,
        password,
        label,
        display_name: displayName,
        imap_host: imapHost,
        imap_port: imapPort,
        imap_ssl: imapSsl,
        smtp_host: smtpHost,
        smtp_port: smtpPort,
        smtp_ssl: smtpSsl,
        smtp_starttls: smtpStarttls,
        is_default: isDefault,
        signature_text: signatureText,
        signature_html: signatureHtml,
      };
      if (isNew) {
        await emailApi.createAccount(payload);
      } else if (account) {
        // Не передаём пустой пароль при редактировании
        const { password: _, ...rest } = payload;
        const updatePayload: any = rest;
        if (password) updatePayload.password = password;
        await emailApi.updateAccount(account.id, updatePayload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="email-form-overlay" onClick={onClose}>
      <div className="email-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-form-header">
          <h3>{isNew ? 'Новый почтовый аккаунт' : `Редактирование: ${account?.email}`}</h3>
          <button className="composer-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="email-form-body">
          <div className="form-row">
            <label>Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@yandex.ru"
              disabled={!isNew}
            />
            {isNew && (
              <button className="btn-secondary" onClick={handleAutodetect}>
                🔍 Определить
              </button>
            )}
          </div>

          <div className="form-row">
            <label>{isNew ? 'Пароль *' : 'Новый пароль'}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isNew ? 'Пароль приложения IMAP' : 'Оставьте пустым чтобы не менять'}
              autoComplete="new-password"
            />
          </div>

          <div className="form-row">
            <label>Имя метки</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="например, Рабочая"
            />
          </div>

          <div className="form-row">
            <label>Отображаемое имя</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Иван Петров"
            />
          </div>

          <div className="form-row">
            <label></label>
            <label className="form-check">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              Использовать как аккаунт по умолчанию
            </label>
          </div>

          <h4>Подпись</h4>
          <div className="form-row" style={{ alignItems: 'flex-start' }}>
            <label>Подпись (текст)</label>
            <textarea
              value={signatureText}
              onChange={(e) => setSignatureText(e.target.value)}
              placeholder={'Например:\nС уважением, Николай Шишанов\nIT Услуги  |  +7 (...)'}
              rows={4}
              style={{ flex: 1, padding: 7, border: '1px solid #d0d3d7', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
          <div className="form-row" style={{ alignItems: 'flex-start' }}>
            <label>Подпись (HTML)</label>
            <textarea
              value={signatureHtml}
              onChange={(e) => setSignatureHtml(e.target.value)}
              placeholder={'<b>Николай Шишанов</b><br>IT Услуги &nbsp;|&nbsp; +7 (...)'}
              rows={4}
              style={{ flex: 1, padding: 7, border: '1px solid #d0d3d7', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          <button className="form-advanced-toggle" onClick={() => setAdvanced((v) => !v)}>
            {advanced ? '▼' : '▶'} Расширенные настройки (IMAP / SMTP)
          </button>

          {advanced && (
            <>
              <h4>IMAP</h4>
              <div className="form-row">
                <label>Хост</label>
                <input value={imapHost} onChange={(e) => setImapHost(e.target.value)} />
              </div>
              <div className="form-row">
                <label>Порт</label>
                <input
                  type="number"
                  value={imapPort}
                  onChange={(e) => setImapPort(Number(e.target.value))}
                />
                <label className="form-check">
                  <input type="checkbox" checked={imapSsl} onChange={(e) => setImapSsl(e.target.checked)} />
                  SSL
                </label>
              </div>

              <h4>SMTP</h4>
              <div className="form-row">
                <label>Хост</label>
                <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
              </div>
              <div className="form-row">
                <label>Порт</label>
                <input
                  type="number"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(Number(e.target.value))}
                />
                <label className="form-check">
                  <input type="checkbox" checked={smtpSsl} onChange={(e) => setSmtpSsl(e.target.checked)} />
                  SSL
                </label>
                <label className="form-check">
                  <input
                    type="checkbox"
                    checked={smtpStarttls}
                    onChange={(e) => setSmtpStarttls(e.target.checked)}
                  />
                  STARTTLS
                </label>
              </div>
            </>
          )}

          {error && <div className="email-form-error">⚠️ {error}</div>}
        </div>

        <div className="email-form-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Сохранение...' : '💾 Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
